import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Award, Wallet, Calendar, Heart, Sparkles, Upload, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, euro, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ClientDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [tab, setTab] = useState("overview");
  const [drop, setDrop] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploadingW, setUploadingW] = useState(false);
  const [services, setServices] = useState([]);
  const [submitPhoto, setSubmitPhoto] = useState(null);
  const [submitForm, setSubmitForm] = useState({ service_id: "", title: "", caption: "", attribution: "anonymous" });
  const [giftOptions, setGiftOptions] = useState(null);
  const [giftCards, setGiftCards] = useState({ bought: [], received: [] });
  const [buyForm, setBuyForm] = useState({ amount: 25, recipient_email: "", message: "" });
  const [redeemCode, setRedeemCode] = useState("");
  const [buying, setBuying] = useState(false);
  const [printCard, setPrintCard] = useState(null);

  const load = () => {
    api.get("/my/kima-id").then((r) => setData(r.data)).catch(() => {});
    api.get("/my/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/my/wallet").then((r) => setWallet(r.data)).catch(() => {});
    api.get("/my/wardrobe").then((r) => setPhotos(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/gift-cards/options").then((r) => setGiftOptions(r.data)).catch(() => {});
    api.get("/my/gift-cards").then((r) => setGiftCards(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const buyGiftCard = async () => {
    const amount = Number(buyForm.amount);
    if (!amount || amount < 10) { toast.error("Importo minimo 10€"); return; }
    setBuying(true);
    try {
      await api.post("/my/gift-cards", { amount_eur: amount, recipient_email: buyForm.recipient_email || null, message: buyForm.message || null });
      toast.success("Buono regalo creato!");
      setBuyForm({ amount: 25, recipient_email: "", message: "" });
      api.get("/my/gift-cards").then((r) => setGiftCards(r.data));
      api.get("/gift-cards/options").then((r) => setGiftOptions(r.data));
      api.get("/my/wallet").then((r) => setWallet(r.data));
      api.get("/my/kima-id").then((r) => setData(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBuying(false); }
  };
  const redeemToCredit = async () => {
    if (!redeemCode.trim()) { toast.error("Inserisci un codice"); return; }
    try {
      const { data: r } = await api.post("/gift-cards/redeem-to-credit", { code: redeemCode.trim() });
      toast.success(`Riscattati ${euro(r.credited_eur)} in KIMA Credit`);
      setRedeemCode("");
      api.get("/my/gift-cards").then((res) => setGiftCards(res.data));
      api.get("/my/wallet").then((res) => setWallet(res.data));
      api.get("/my/kima-id").then((res) => setData(res.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const copyCode = (code) => { navigator.clipboard?.writeText(code); toast.success("Codice copiato"); };

  const uploadWardrobe = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setUploadingW(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/my/wardrobe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto aggiunta al tuo Wardrobe");
      api.get("/my/wardrobe").then((r) => setPhotos(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel caricamento"); }
    finally { setUploadingW(false); }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Eliminare questa foto dal tuo archivio?")) return;
    try { await api.delete(`/my/wardrobe/${id}`); toast.success("Foto eliminata"); setPhotos((p) => p.filter((x) => x.id !== id)); }
    catch { toast.error("Errore"); }
  };

  const openSubmit = (photo) => {
    setSubmitForm({ service_id: photo.service_id || "", title: "", caption: "", attribution: "anonymous" });
    setSubmitPhoto(photo);
  };
  const submitToLooks = async () => {
    try {
      await api.post("/my/looks/submit", { wardrobe_photo_id: submitPhoto.id, ...submitForm });
      toast.success("Proposta inviata! Sarà pubblicata dopo l'approvazione del salone.");
      setSubmitPhoto(null);
      api.get("/my/wardrobe").then((r) => setPhotos(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const claim = async () => {
    setSpinning(true);
    try {
      await new Promise((r) => setTimeout(r, 1400));
      const { data: c } = await api.post("/daily-drop/claim");
      setDrop(c);
      toast.success("Premio ritirato!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSpinning(false); }
  };

  if (!data) return <div className="py-32 text-center text-taupe font-serif text-2xl">Caricamento…</div>;
  const tabs = [["overview", "Panoramica"], ["appointments", "Appuntamenti"], ["wardrobe", "KIMA Wardrobe"], ["wallet", "Wallet"], ["giftcards", "Buoni Regalo"], ["drop", "Daily Drop"]];

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <span className="kima-overline text-burgundy">{data.user.kima_id}</span>
      <h1 className="font-serif text-4xl md:text-5xl text-espresso mt-2 mb-8">Ciao, {data.user.name}</h1>

      <div className="flex flex-wrap gap-2 mb-10 border-b border-espresso/10">
        {tabs.map(([k, l]) => (<button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`} className={`px-4 py-3 text-sm transition-colors border-b-2 -mb-px ${tab === k ? "border-burgundy text-espresso" : "border-transparent text-taupe hover:text-espresso"}`}>{l}</button>))}
      </div>

      {tab === "overview" && (
        <div className="grid md:grid-cols-3 gap-5">
          <Card icon={Calendar} title="Prossimo appuntamento">
            {data.next_appointment ? <div><div className="font-serif text-2xl text-espresso">{data.next_appointment.service_name}</div><div className="text-sm text-espresso/60 mt-1">{fmtDate(data.next_appointment.date)} · {data.next_appointment.start_time} · {data.next_appointment.artist_name}</div></div> : <p className="text-espresso/50 text-sm">Nessun appuntamento. <Link to="/prenota" className="text-burgundy underline">Prenota ora</Link></p>}
          </Card>
          <Card icon={Award} title="KIMA Club"><div className="font-serif text-3xl text-espresso">{data.loyalty.points} <span className="text-lg">punti</span></div><div className="text-sm text-taupe mt-1">Livello {data.loyalty.level}</div></Card>
          <Card icon={Wallet} title="KIMA Credit"><div className="font-serif text-3xl text-espresso">{euro(data.credit.balance)}</div><div className="text-xs text-taupe mt-1">Credito promozionale non convertibile</div></Card>
          <Card icon={Gift} title="Daily Drop">{data.daily_drop_available ? <button onClick={() => setTab("drop")} className="text-burgundy underline text-sm">Ritira il premio di oggi →</button> : <p className="text-espresso/50 text-sm">Premio di oggi già ritirato</p>}</Card>
          <Card icon={Heart} title="Look salvati"><div className="font-serif text-3xl text-espresso">{data.saved_looks.length}</div></Card>
          <Card icon={Sparkles} title="KIMA Wardrobe"><div className="font-serif text-3xl text-espresso">{photos.length}</div><div className="text-xs text-taupe mt-1">Foto nel tuo archivio</div></Card>
        </div>
      )}

      {tab === "appointments" && (
        <div className="space-y-3">
          {bookings.length === 0 && <p className="text-espresso/50">Nessuna prenotazione.</p>}
          {bookings.map((b) => (
            <div key={b.id} className="border border-espresso/10 bg-offwhite rounded-sm p-5 flex justify-between items-center flex-wrap gap-3">
              <div><div className="font-serif text-xl text-espresso">{b.service_name}</div><div className="text-sm text-espresso/60">{fmtDate(b.date)} · {b.start_time} · {b.artist_name}</div></div>
              <div className="text-right"><StatusBadge s={b.status} /><div className="text-sm text-espresso/60 mt-1">{euro(b.price)}</div></div>
            </div>
          ))}
        </div>
      )}

      {tab === "wardrobe" && (
        <div data-testid="wardrobe-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h3 className="font-serif text-2xl text-espresso">Il tuo archivio privato</h3>
              <p className="text-sm text-espresso/60">Le foto dei tuoi look, visibili solo a te e al salone. Puoi proporle alla community KIMA Looks.</p>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer bg-espresso text-ivory text-sm px-5 py-2.5 rounded-sm hover:bg-burgundy transition-colors">
              <Upload size={16} /> {uploadingW ? "Caricamento…" : "Carica foto"}
              <input type="file" accept="image/*" data-testid="wardrobe-upload" className="hidden" onChange={(e) => uploadWardrobe(e.target.files)} />
            </label>
          </div>

          {photos.length === 0 && <p className="text-espresso/50 mb-8">Nessuna foto nel tuo archivio. Carica il tuo primo look.</p>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {photos.map((p) => (
              <div key={p.id} data-testid={`wardrobe-photo-${p.id}`} className="border border-espresso/10 rounded-sm overflow-hidden bg-offwhite group">
                <div className="relative">
                  <img src={p.image_url} alt={p.caption || "look"} className="w-full aspect-square object-cover" />
                  <button onClick={() => deletePhoto(p.id)} data-testid={`wardrobe-delete-${p.id}`} className="absolute top-2 right-2 bg-offwhite/90 p-2 rounded-full text-kimaerror hover:bg-kimaerror hover:text-ivory transition-colors"><Trash2 size={14} /></button>
                </div>
                <div className="p-3">
                  <div className="text-xs text-taupe mb-1">{p.uploaded_by_role !== "client" ? "Aggiunta dal salone" : "Tua foto"} · {fmtDate((p.created_at || "").slice(0, 10))}</div>
                  {p.caption && <div className="text-sm text-espresso mb-2">{p.caption}</div>}
                  {p.submitted_to_looks ? (
                    <span className="text-xs text-burgundy">Proposta a KIMA Looks</span>
                  ) : (
                    <button onClick={() => openSubmit(p)} data-testid={`wardrobe-submit-${p.id}`} className="text-xs text-espresso underline hover:text-burgundy transition-colors">Proponi a KIMA Looks →</button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {(data.saved_looks || []).length > 0 && (
            <div className="mt-14">
              <h3 className="font-serif text-2xl text-espresso mb-1">Look salvati</h3>
              <p className="text-sm text-espresso/60 mb-5">Ispirazioni salvate da KIMA Looks e dalla galleria.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {data.saved_looks.map((l) => (<div key={l.id} className="border border-espresso/10 rounded-sm overflow-hidden"><img src={l.image} alt={l.title} className="w-full aspect-square object-cover" /><div className="p-3 font-serif text-lg text-espresso truncate">{l.title}</div></div>))}
              </div>
            </div>
          )}

          {submitPhoto && (
            <div className="fixed inset-0 bg-espresso/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSubmitPhoto(null)}>
              <div className="bg-offwhite rounded-sm p-8 w-full max-w-md animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="submit-look-modal">
                <div className="flex justify-between items-start mb-5"><h3 className="font-serif text-2xl text-espresso">Proponi a KIMA Looks</h3><button onClick={() => setSubmitPhoto(null)}><X size={20} className="text-taupe" /></button></div>
                <img src={submitPhoto.image_url} alt="look" className="w-full aspect-video object-cover rounded-sm mb-5" />
                <div className="space-y-4">
                  <div><label className="kima-overline text-taupe">Titolo</label><input value={submitForm.title} data-testid="submit-look-title" onChange={(e) => setSubmitForm({ ...submitForm, title: e.target.value })} placeholder="es. French glow autunnale" className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso text-espresso" /></div>
                  <div><label className="kima-overline text-taupe">Servizio collegato</label>
                    <select value={submitForm.service_id} data-testid="submit-look-service" onChange={(e) => setSubmitForm({ ...submitForm, service_id: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso text-espresso">
                      <option value="">Nessuno</option>
                      {services.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                    </select>
                  </div>
                  <div><label className="kima-overline text-taupe">Come vuoi apparire?</label>
                    <div className="flex gap-2 mt-2">
                      {[["anonymous", "Anonimo"], ["name", "Con il mio nome"]].map(([k, l]) => (
                        <button key={k} onClick={() => setSubmitForm({ ...submitForm, attribution: k })} data-testid={`submit-attr-${k}`} className={`px-4 py-2 rounded-sm text-sm border transition-colors ${submitForm.attribution === k ? "bg-espresso text-ivory border-espresso" : "border-espresso/20 hover:border-espresso"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-espresso/50 mt-5">La tua proposta sarà pubblicata solo dopo l'approvazione del salone.</p>
                <div className="flex gap-3 mt-6">
                  <button onClick={submitToLooks} data-testid="submit-look-confirm" className="flex-1 bg-espresso text-ivory py-3 rounded-sm hover:bg-burgundy transition-colors text-sm">Invia proposta</button>
                  <button onClick={() => setSubmitPhoto(null)} className="px-6 py-3 border border-espresso/20 rounded-sm hover:border-espresso transition-colors text-sm">Annulla</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "wallet" && wallet && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border border-espresso/10 bg-offwhite rounded-sm p-6"><div className="kima-overline text-taupe mb-3">Punti fedeltà</div><div className="font-serif text-3xl text-espresso mb-4">{wallet.loyalty.points || 0} punti</div>{(wallet.loyalty_tx || []).map((t, i) => (<div key={i} className="flex justify-between text-sm border-t border-espresso/5 py-2"><span className="text-espresso/60">{t.reason}</span><span className="text-kimasuccess">+{t.points}</span></div>))}</div>
          <div className="border border-espresso/10 bg-offwhite rounded-sm p-6"><div className="kima-overline text-taupe mb-3">KIMA Credit</div><div className="font-serif text-3xl text-espresso mb-2">{euro(wallet.credit.balance || 0)}</div><p className="text-xs text-espresso/50">Utilizzabile solo su acquisti idonei, non convertibile in denaro.</p></div>
          <div className="border border-espresso/10 bg-offwhite rounded-sm p-6 md:col-span-2"><div className="kima-overline text-taupe mb-3">Servizi prepagati</div>{(wallet.prepaid || []).length ? wallet.prepaid.map((p, i) => (<div key={i} className="flex justify-between text-sm py-2"><span>{p.name}</span><span>{p.remaining_uses} usi rimanenti</span></div>)) : <p className="text-espresso/50 text-sm">Nessun servizio prepagato attivo.</p>}</div>
        </div>
      )}

      {tab === "giftcards" && (
        <div data-testid="giftcards-panel" className="grid lg:grid-cols-2 gap-8">
          <div className="border border-espresso/10 bg-offwhite rounded-sm p-6">
            <h3 className="font-serif text-2xl text-espresso mb-1">Regala un buono KIMA</h3>
            <p className="text-sm text-espresso/60 mb-5">Converti i tuoi punti in un buono regalo. {giftOptions ? `Hai ${giftOptions.points} punti (valore ${euro(giftOptions.usable_eur)}).` : ""}</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {(giftOptions?.presets || [10, 25, 50]).map((v) => (
                <button key={v} onClick={() => setBuyForm({ ...buyForm, amount: v })} data-testid={`gift-preset-${v}`} className={`px-5 py-2 rounded-sm text-sm border transition-colors ${Number(buyForm.amount) === v ? "bg-espresso text-ivory border-espresso" : "border-espresso/20 hover:border-espresso"}`}>{euro(v)}</button>
              ))}
            </div>
            <div className="space-y-4">
              <div><label className="kima-overline text-taupe">Importo personalizzato (min 10€)</label><input type="number" min="10" value={buyForm.amount} data-testid="gift-amount" onChange={(e) => setBuyForm({ ...buyForm, amount: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso text-espresso" /></div>
              <div><label className="kima-overline text-taupe">Email destinatario (facoltativa)</label><input type="email" value={buyForm.recipient_email} data-testid="gift-recipient" onChange={(e) => setBuyForm({ ...buyForm, recipient_email: e.target.value })} placeholder="Se ha un account KIMA riceverà il buono" className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso text-espresso" /></div>
              <div><label className="kima-overline text-taupe">Messaggio (facoltativo)</label><input value={buyForm.message} data-testid="gift-message" onChange={(e) => setBuyForm({ ...buyForm, message: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso text-espresso" /></div>
            </div>
            <p className="text-xs text-espresso/50 mt-4">Costo: {giftOptions ? Math.round(Number(buyForm.amount || 0) / (giftOptions.points_to_eur || 0.01)) : "—"} punti · valore {euro(Number(buyForm.amount || 0))}</p>
            <button onClick={buyGiftCard} disabled={buying} data-testid="gift-buy" className="mt-5 w-full bg-espresso text-ivory py-3 rounded-sm hover:bg-burgundy transition-colors disabled:opacity-50 text-sm">{buying ? "Creazione…" : "Crea buono regalo con i punti"}</button>

            <div className="border-t border-espresso/10 mt-6 pt-6">
              <h4 className="font-serif text-lg text-espresso mb-2">Hai ricevuto un codice?</h4>
              <div className="flex gap-2">
                <input value={redeemCode} data-testid="gift-redeem-code" onChange={(e) => setRedeemCode(e.target.value)} placeholder="KIMA-XXXX-XXXX" className="flex-1 border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso text-espresso uppercase" />
                <button onClick={redeemToCredit} data-testid="gift-redeem-btn" className="bg-burgundy text-ivory px-5 py-2 rounded-sm text-sm hover:bg-espresso transition-colors">Riscatta</button>
              </div>
              <p className="text-xs text-espresso/50 mt-2">Il valore viene aggiunto al tuo KIMA Credit, oppure inserisci il codice al checkout per pagare l'acconto.</p>
            </div>
          </div>

          <div>
            <h3 className="font-serif text-2xl text-espresso mb-4">I tuoi buoni</h3>
            {giftCards.bought.length === 0 && giftCards.received.length === 0 && <p className="text-espresso/50 text-sm">Nessun buono regalo ancora.</p>}
            <div className="space-y-3">
              {[...giftCards.received.map((c) => ({ ...c, _kind: "received" })), ...giftCards.bought.map((c) => ({ ...c, _kind: "bought" }))].map((c) => (
                <div key={c.id} data-testid={`gift-card-${c.id}`} className="border border-espresso/10 bg-offwhite rounded-sm p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-serif text-2xl text-espresso">{euro(c.balance_eur)} <span className="text-sm text-taupe">/ {euro(c.amount_eur)}</span></div>
                      <div className="text-xs text-taupe mt-1">{c._kind === "received" ? `Da ${c.buyer_name}` : (c.recipient_email ? `Per ${c.recipient_email}` : "Da regalare")}</div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-sm ${c.status === "redeemed" ? "bg-espresso/10 text-espresso/60" : "bg-kimasuccess/15 text-kimasuccess"}`}>{c.status === "redeemed" ? "Usato" : c.status === "partial" ? "Parziale" : "Attivo"}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <code className="flex-1 text-sm bg-ivory border border-espresso/10 rounded-sm px-3 py-2 tracking-wider text-espresso" data-testid={`gift-code-${c.id}`}>{c.code}</code>
                    <button onClick={() => copyCode(c.code)} data-testid={`gift-copy-${c.id}`} className="text-xs text-espresso underline hover:text-burgundy">Copia</button>
                    <button onClick={() => setPrintCard(c)} data-testid={`gift-print-${c.id}`} className="text-xs text-espresso underline hover:text-burgundy">Stampa</button>
                  </div>
                  {c.message && <p className="text-xs text-espresso/60 mt-2 italic">"{c.message}"</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {printCard && (
        <div className="fixed inset-0 bg-espresso/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 kima-no-print" onClick={() => setPrintCard(null)}>
          <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="kima-print-area bg-ivory rounded-sm overflow-hidden border border-espresso/10" data-testid="gift-print-modal">
              <div className="bg-espresso text-ivory px-8 py-6 text-center">
                <div className="tracking-[0.3em] text-sm">KIMA NAILS</div>
                <div className="font-serif text-3xl mt-2">Buono Regalo</div>
              </div>
              <div className="p-8 text-center">
                <div className="kima-overline text-taupe">Valore</div>
                <div className="font-serif text-5xl text-espresso my-2">{euro(printCard.amount_eur)}</div>
                {printCard.message && <p className="text-espresso/70 italic font-serif text-lg my-5">"{printCard.message}"</p>}
                <div className="border-t border-b border-espresso/10 py-5 my-5">
                  <div className="kima-overline text-taupe mb-2">Codice</div>
                  <div className="font-mono text-2xl tracking-[0.2em] text-burgundy">{printCard.code}</div>
                </div>
                <p className="text-xs text-espresso/50">Inserisci il codice al momento della prenotazione su KIMA, oppure riscattalo come KIMA Credit dal tuo profilo. Saldo residuo: {euro(printCard.balance_eur)}.</p>
                <div className="mt-6 text-xs text-taupe">Un regalo da {printCard.buyer_name || "un amico"} · KIMA Nails Sassari</div>
              </div>
            </div>
            <div className="flex gap-3 mt-4 kima-no-print">
              <button onClick={() => window.print()} data-testid="gift-print-btn" className="flex-1 bg-espresso text-ivory py-3 rounded-sm hover:bg-burgundy transition-colors text-sm">Stampa / Salva PDF</button>
              <button onClick={() => setPrintCard(null)} className="px-6 py-3 border border-ivory/40 text-ivory rounded-sm hover:bg-ivory/10 transition-colors text-sm">Chiudi</button>
            </div>
          </div>
        </div>
      )}

      {tab === "drop" && (
        <div className="max-w-md mx-auto text-center">
          <h2 className="font-serif text-3xl text-espresso mb-2">KIMA Daily Drop</h2>
          <p className="text-espresso/60 text-sm mb-8">Un beneficio riservato ogni giorno. Un ritiro per cliente al giorno.</p>
          <div className={`w-56 h-56 mx-auto rounded-full border-4 border-burgundy flex items-center justify-center transition-transform duration-1000 ${spinning ? "rotate-[1080deg]" : ""}`} style={{ background: "conic-gradient(#6B2835,#A59689,#211C19,#6B2835)" }}>
            <div className="w-40 h-40 rounded-full bg-offwhite flex items-center justify-center p-4 text-center">{drop ? <span className="text-espresso text-sm font-medium">{drop.reward}</span> : <Gift className="text-burgundy" size={40} strokeWidth={1} />}</div>
          </div>
          {drop ? (
            <div className="mt-8 border border-espresso/10 bg-offwhite rounded-sm p-5"><div className="font-serif text-xl text-espresso">{drop.reward}</div><div className="text-xs text-taupe mt-1">Valido fino al {fmtDate(drop.expires)}</div></div>
          ) : data.daily_drop_available ? (
            <button onClick={claim} disabled={spinning} data-testid="claim-drop" className="mt-8 bg-espresso text-ivory px-10 py-4 rounded-sm hover:bg-burgundy transition-colors disabled:opacity-50">{spinning ? "Estrazione…" : "Ritira il premio di oggi"}</button>
          ) : (<p className="mt-8 text-espresso/50">Hai già ritirato il premio di oggi. Torna domani!</p>)}
        </div>
      )}
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (<div className="border border-espresso/10 bg-offwhite rounded-sm p-6"><div className="flex items-center gap-2 text-taupe mb-3"><Icon size={18} strokeWidth={1.4} /><span className="kima-overline">{title}</span></div>{children}</div>);
}
export function StatusBadge({ s }) {
  const map = { confirmed: ["Confermato", "bg-kimasuccess/15 text-kimasuccess"], awaiting_payment: ["In attesa pagamento", "bg-taupe/20 text-espresso"], completed: ["Completato", "bg-espresso/10 text-espresso"], pending: ["In sospeso", "bg-taupe/20 text-espresso"], no_show: ["No-show", "bg-kimaerror/15 text-kimaerror"], blocked: ["Bloccato", "bg-espresso text-ivory"], checked_in: ["Check-in", "bg-taupe/20 text-espresso"], in_progress: ["In corso", "bg-burgundy/15 text-burgundy"], cancelled_by_salon: ["Annullato", "bg-kimaerror/15 text-kimaerror"] };
  const [l, c] = map[s] || [s, "bg-softgrey text-espresso"];
  return <span className={`text-xs px-3 py-1 rounded-sm ${c}`}>{l}</span>;
}
